// Demo charts on the tools pages: synthetic data generated client-side,
// rendered by velari (candlestick / line / area / baseline per data-demo).
import { createChart, areaSeries, baselineSeries, candlestickSeries, lineSeries } from 'velari';
import type { ChartOptions, SeriesOptions } from 'velari';
import { chartTheme, registerCharts } from '../core/theme';

const POINTS = 160;
const HOUR = 3_600_000;

/** Mulberry32 — small seeded PRNG so every visit shows the same pretty data. */
function mulberry(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function timeline(): Float64Array {
  const now = Date.now();
  const x = new Float64Array(POINTS);
  for (let i = 0; i < POINTS; i++) x[i] = now - (POINTS - 1 - i) * HOUR;
  return x;
}

function walk(seed: number, start: number, volatility: number): Float64Array {
  const random = mulberry(seed);
  const values = new Float64Array(POINTS);
  let level = start;
  let trend = 0;
  for (let i = 0; i < POINTS; i++) {
    if (i % 16 === 0) trend = (random() - 0.5) * volatility * 0.7;
    level = Math.max(start * 0.4, level + (random() - 0.5) * volatility + trend);
    values[i] = level;
  }
  return values;
}

function candles(seed: number): { open: Float64Array; high: Float64Array; low: Float64Array; close: Float64Array } {
  const random = mulberry(seed);
  const close = walk(seed + 7, 187, 3.4);
  const open = new Float64Array(POINTS);
  const high = new Float64Array(POINTS);
  const low = new Float64Array(POINTS);
  for (let i = 0; i < POINTS; i++) {
    open[i] = i === 0 ? close[0]! : close[i - 1]!;
    const body = Math.max(open[i]!, close[i]!);
    const belly = Math.min(open[i]!, close[i]!);
    high[i] = body + random() * 1.6;
    low[i] = belly - random() * 1.6;
  }
  return { open, high, low, close };
}

function series(kind: string): SeriesOptions {
  switch (kind) {
    case 'candlestick':
      return { name: 'AMGA', type: candlestickSeries, values: candles(20260716) };
    case 'line':
      return { name: 'BTC', type: lineSeries, values: walk(42, 64_000, 900), width: 2 };
    case 'baseline': {
      const pnl = walk(1337, 0, 260);
      for (let i = 0; i < pnl.length; i++) pnl[i]! -= 130;
      return { name: 'P/L', type: baselineSeries, values: pnl, baseline: 0 };
    }
    default:
      return { name: 'EUR/USD', type: areaSeries, values: walk(7, 1.085, 0.004), width: 2 };
  }
}

for (const host of document.querySelectorAll<HTMLElement>('[data-demo]')) {
  const options: ChartOptions = {
    data: { x: timeline(), series: [series(host.dataset.demo ?? 'area')] },
    xType: 'time',
    legend: false,
    theme: chartTheme(),
  };
  registerCharts(createChart(host, options));
}
